import { useEffect } from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  IntegrationsSyncableStateFieldsFragment,
  IntegrationsSyncableStateStatusEnum,
  ReadLeaveSyncableStateDocument,
} from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useIntegrationsSyncContext } from 'modules/integrations/public'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

import Failed from './StatusContent/Failed'
import Outdated from './StatusContent/Outdated'
import Pending from './StatusContent/Pending'
import Synced from './StatusContent/Synced'
import Syncing from './StatusContent/Syncing'

type LeaveSyncBannerStatus = IntegrationsSyncableStateStatusEnum | 'pending'

const resolveStatus = (
  syncableState: IntegrationsSyncableStateFieldsFragment | null | undefined
): LeaveSyncBannerStatus => {
  if (!syncableState) return 'pending'

  switch (syncableState.status) {
    case 'failed':
    case 'invalid':
      return 'failed'
    default:
      return syncableState.status
  }
}

const LeaveSyncBanner = ({ incidenceId, leaveId }: { incidenceId: string; leaveId: string }) => {
  const { setContextParameters, marketplaceIntegration, syncEnabled, isSyncing } =
    useIntegrationsSyncContext()
  // Using private context to avoid exposing framework internals.
  // this FE is temporal until new Employee Update view is revamped and
  // we redesign reusable components inside the framework for all Employee Updates types
  const { runSynchronisation, isBookkeeperView } = usePrivateIntegrationsSyncContext()

  useEffect(() => {
    setContextParameters({ incidence_id: incidenceId })
  }, [incidenceId, setContextParameters])

  const [fetchLeaveSyncableState, { data: syncableStateData, loading: syncableStateLoading }] =
    useLazyQuery(ReadLeaveSyncableStateDocument)

  useEffect(() => {
    if (!syncEnabled || !marketplaceIntegration) return

    fetchLeaveSyncableState({
      variables: {
        leaveId,
        marketplaceIntegrationId: marketplaceIntegration.id,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [syncEnabled, marketplaceIntegration, leaveId, isBookkeeperView, fetchLeaveSyncableState])

  const syncableState: IntegrationsSyncableStateFieldsFragment | null =
    syncableStateData?.timeoff?.leavesConnection?.nodes?.[0]?.syncableState?.[0] ?? null

  const status = resolveStatus(syncableState)
  const integrationName = marketplaceIntegration?.displayName ?? ''

  const syncContent = () => {
    if (isSyncing) return <Syncing integrationName={integrationName} />

    switch (status) {
      case 'pending':
        return (
          <Pending
            integrationName={integrationName}
            onSync={runSynchronisation}
            syncing={isSyncing}
          />
        )
      case 'synced':
        return <Synced integrationName={integrationName} />
      case 'failed':
        return (
          <Failed
            integrationName={integrationName}
            errorMessages={syncableState?.errorMessages ?? []}
            onSync={runSynchronisation}
            syncing={isSyncing}
          />
        )
      case 'outdated':
        return (
          <Outdated
            integrationName={integrationName}
            onSync={runSynchronisation}
            syncing={isSyncing}
            isDeleted={!!syncableState?.syncableDeletedAt}
          />
        )
      case 'syncing':
        return <Syncing integrationName={integrationName} />
      default:
        throw new Error(`Rendering unknown status ${status}`)
    }
  }

  if (!syncEnabled) return null

  if (syncableStateLoading) {
    return (
      <Box width="full" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  return <>{syncContent()}</>
}

export default LeaveSyncBanner
